import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import { HeartPulse, Dumbbell, Microscope, ArrowRight, CheckCircle2 } from "lucide-react";

const CHECKLISTS = {
  nurse: {
    Icon: HeartPulse,
    eyebrow: "Nurse Competency",
    h1: "Nurse skill checklists, specialty by specialty.",
    lead: "Research-backed, self-assessment-first competency checklists covering ICU, ER, Med-Surg, OR, Cath Lab, L&D, Pediatrics, PACU and more.",
    items: [
      "ICU — general, cardiac, neuro, trauma",
      "ER — Level I trauma, community, pediatric",
      "Med-Surg — tele, step-down, ortho",
      "OR — circulator, scrub, robotic",
      "Cath Lab — interventional, EP",
      "L&D — low-risk, high-risk, triage",
      "Pediatrics — acute, PICU, NICU",
      "PACU — Phase I & II",
      "Dialysis — acute & chronic",
      "Oncology — infusion & inpatient",
      "Behavioral Health — acute psych",
      "Home Health — admissions & chronic",
    ],
  },
  therapist: {
    Icon: Dumbbell,
    eyebrow: "Therapy Competency",
    h1: "Therapy checklists across the care continuum.",
    lead: "Physical, occupational and speech-language pathology competency checklists covering acute, SNF, outpatient, pediatric and home-health settings.",
    items: [
      "Physical Therapy — acute, outpatient ortho, SNF",
      "Physical Therapy — pediatric & early-intervention",
      "Occupational Therapy — acute & outpatient",
      "Occupational Therapy — pediatric & school-based",
      "Speech-Language Pathology — adult acute",
      "Speech-Language Pathology — pediatric & SNF",
    ],
  },
  other: {
    Icon: Microscope,
    eyebrow: "Allied Competency",
    h1: "Allied health and other clinical checklists.",
    lead: "Respiratory, radiology, laboratory, surgical tech and imaging competency checklists — mapped to facility-specific requirements and vendor-neutral.",
    items: [
      "Respiratory Therapy — acute & NICU",
      "Radiology — CT, MRI, Ultrasound",
      "Radiology — Mammography, Nuc Med",
      "Laboratory — MLS, MLT, blood bank",
      "Surgical Tech — general, ortho, cardiac",
      "Echo & Vascular",
      "Pharmacy Tech — IV room & inpatient",
      "EEG / EMG Tech",
      "Wound Care — certified specialists",
    ],
  },
};

const ORDER = ["nurse", "therapist", "other"];
const LABELS = { nurse: "Nurse", therapist: "Therapist", other: "Other" };

export default function ChecklistDetail() {
  const { slug } = useParams();
  const c = CHECKLISTS[slug];
  if (!c) return <Navigate to="/compliance/checklists" replace />;
  const idx = ORDER.indexOf(slug);
  const prev = idx > 0 ? ORDER[idx - 1] : null;
  const next = idx < ORDER.length - 1 ? ORDER[idx + 1] : null;
  const Icon = c.Icon;

  return (
    <div className="min-h-screen bg-white" data-testid={`checklist-detail-${slug}`}>
      <PublicNav />

      {/* Hero */}
      <section className="bg-trital-mesh">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-6">
            <Link to="/compliance" className="hover:text-trital-navy">Compliance</Link>
            <span className="mx-2 text-slate-300">/</span>
            <Link to="/compliance/checklists" className="hover:text-trital-navy">Checklists</Link>
            <span className="mx-2 text-slate-300">/</span>
            <span className="text-slate-700">{LABELS[slug]}</span>
          </nav>
          <div className="flex items-start gap-5 max-w-4xl">
            <div className="shrink-0 w-14 h-14 rounded-2xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
              <Icon className="w-7 h-7" strokeWidth={1.8} />
            </div>
            <div>
              <div className="eyebrow text-trital-red">{c.eyebrow}</div>
              <h1 className="mt-3 font-display font-black text-4xl lg:text-[56px] leading-[1.05] text-slate-900 text-balance">
                {c.h1}
              </h1>
              <p className="mt-5 text-[17px] leading-relaxed text-slate-600 max-w-[60ch]">{c.lead}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register?role=clinician" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep" data-testid="checklist-cta-start">
                  Start a checklist <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
                <Link to="/compliance/checklists" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy text-sm font-semibold btn-lift hover:border-trital-navy hover:bg-slate-50">
                  All checklists
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Items */}
      <section className="section-pad">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="max-w-[720px] mb-10">
            <div className="eyebrow text-trital-red mb-3">Available checklists</div>
            <h2 className="font-display font-black text-3xl text-slate-900 text-balance">
              Specialty coverage.
            </h2>
          </div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.items.map((it) => (
              <li key={it} className="card-premium p-5 rounded-xl flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-trital-red shrink-0 mt-0.5" strokeWidth={1.8} />
                <span className="text-[15px] text-slate-800">{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Prev / Next */}
      <section className="section-pad-sm">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-4">
          {prev ? (
            <Link to={`/compliance/checklists/${prev}`} className="group card-premium card-premium-hover p-6 rounded-2xl" data-testid="checklist-prev">
              <div className="eyebrow text-slate-500">Previous</div>
              <div className="mt-2 font-display font-bold text-xl text-slate-900 group-hover:text-trital-navy">← {LABELS[prev]}</div>
            </Link>
          ) : <div />}
          {next ? (
            <Link to={`/compliance/checklists/${next}`} className="group card-premium card-premium-hover p-6 rounded-2xl md:text-right" data-testid="checklist-next">
              <div className="eyebrow text-slate-500">Next</div>
              <div className="mt-2 font-display font-bold text-xl text-slate-900 group-hover:text-trital-navy">{LABELS[next]} →</div>
            </Link>
          ) : <div />}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
